import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, Animated, TouchableOpacity, Dimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastAction {
  readonly label: string;
  readonly onPress: () => void;
  readonly style?: 'default' | 'cancel' | 'destructive';
}

interface ToastConfig {
  readonly type?: ToastType;
  readonly title: string;
  readonly message?: string;
  readonly duration?: number;
  readonly actions?: ToastAction[];
}

interface ToastContextType {
  readonly show: (config: ToastConfig) => void;
  readonly hide: () => void;
  readonly confirm: (title: string, message: string, onConfirm: () => void, onCancel?: () => void) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

/** useToast - access toast show/hide/confirm from any component inside ToastProvider. */
export function useToast(): ToastContextType {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

const iconMap: Record<ToastType, keyof typeof Ionicons.glyphMap> = {
  success: 'checkmark-circle',
  error: 'alert-circle',
  info: 'information-circle',
  warning: 'warning',
};

/** ToastProvider - renders slide-down toast notifications with optional action buttons. */
export function ToastProvider({ children }: { readonly children: React.ReactNode }) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [toast, setToast] = useState<ToastConfig | null>(null);
  const slideAnim = useRef(new Animated.Value(-200)).current;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const colorMap: Record<ToastType, string> = {
    success: colors.success,
    error: colors.danger,
    info: colors.primary,
    warning: colors.warning,
  };

  const hide = useCallback(() => {
    Animated.timing(slideAnim, {
      toValue: -200,
      duration: 250,
      useNativeDriver: true,
    }).start(() => setToast(null));
  }, [slideAnim]);

  const show = useCallback((config: ToastConfig) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast(config);
    slideAnim.setValue(-200);
    Animated.spring(slideAnim, {
      toValue: 0,
      tension: 80,
      friction: 12,
      useNativeDriver: true,
    }).start();

    const duration = config.duration ?? (config.actions ? 0 : 3500);
    if (duration > 0) {
      timerRef.current = setTimeout(hide, duration);
    }
  }, [slideAnim, hide]);

  const confirm = useCallback((title: string, message: string, onConfirm: () => void, onCancel?: () => void) => {
    show({
      type: 'warning',
      title,
      message,
      duration: 0,
      actions: [
        { label: 'Cancel', onPress: () => { hide(); onCancel?.(); }, style: 'cancel' },
        { label: 'Confirm', onPress: () => { hide(); onConfirm(); }, style: 'destructive' },
      ],
    });
  }, [show, hide]);

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const type = toast?.type || 'info';
  const accentColor = colorMap[type];

  return (
    <ToastContext.Provider value={{ show, hide, confirm }}>
      {children}
      {toast && (
        <Animated.View
          style={[
            styles.container,
            { transform: [{ translateY: slideAnim }], borderLeftColor: accentColor },
          ]}
        >
          <TouchableOpacity
            style={styles.content}
            activeOpacity={toast.actions ? 1 : 0.8}
            onPress={toast.actions ? undefined : hide}
          >
            <View style={styles.header}>
              <Ionicons name={iconMap[type]} size={22} color={accentColor} />
              <Text style={styles.title} numberOfLines={2}>{toast.title}</Text>
              {!toast.actions && (
                <TouchableOpacity onPress={hide} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                  <Ionicons name="close" size={20} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>
            {toast.message ? <Text style={styles.message}>{toast.message}</Text> : null}
            {toast.actions && (
              <View style={styles.actions}>
                {toast.actions.map((action, i) => (
                  <TouchableOpacity
                    key={i}
                    style={[
                      styles.actionBtn,
                      action.style === 'destructive' && styles.actionBtnDestructive,
                      action.style === 'cancel' && styles.actionBtnCancel,
                    ]}
                    onPress={action.onPress}
                  >
                    <Text
                      style={[
                        styles.actionText,
                        action.style === 'destructive' && styles.actionTextDestructive,
                        action.style === 'cancel' && styles.actionTextCancel,
                      ]}
                    >
                      {action.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </TouchableOpacity>
        </Animated.View>
      )}
    </ToastContext.Provider>
  );
}

const { width } = Dimensions.get('window');

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: {
    position: 'absolute',
    top: 50,
    left: spacing.md,
    right: spacing.md,
    maxWidth: Math.min(width - spacing.md * 2, 500),
    alignSelf: 'center',
    backgroundColor: c.surface,
    borderRadius: borderRadius.lg,
    borderLeftWidth: 4,
    shadowColor: c.black,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 10,
    zIndex: 9999,
  },
  content: { padding: spacing.md },
  header: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  title: { flex: 1, fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  message: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: spacing.xs, marginLeft: 30, lineHeight: 20 },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.md },
  actionBtn: { paddingVertical: spacing.sm, paddingHorizontal: spacing.lg, borderRadius: borderRadius.sm, backgroundColor: c.primary },
  actionBtnDestructive: { backgroundColor: c.danger },
  actionBtnCancel: { backgroundColor: 'transparent', borderWidth: 1, borderColor: c.border },
  actionText: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.white },
  actionTextDestructive: { color: c.white },
  actionTextCancel: { color: c.textSecondary },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
